import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { StatusBadge } from "../components/StatusBadge";
import { Plus, ArrowRightCircle, Trash2, Search, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { formatIDR, formatDate, formatApiError } from "../lib/format";
import { DIVISIONS, INQUIRY_STATUSES, INQUIRY_STATUS_LABEL, INQUIRY_SOURCES, isManagement, canDelete } from "../lib/constants";
import { waLink, waTemplates } from "../lib/wa";

const EMPTY = {
  bride_name: "", groom_name: "", whatsapp: "", email: "", instagram: "",
  event_name: "", event_date: "", venue: "", location: "", guest_count: 0,
  source: "WhatsApp", status: "New", interest: "", budget_range: "",
  division_id: null, package_interest: "", notes: "", lines: [], client_id: null,
};

const KANBAN_STATUSES = ["New", "Follow-up", "Qualified", "Quotation", "Negotiation", "Accepted"];

export default function Inquiry() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [products, setProducts] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [openDetail, setOpenDetail] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [view, setView] = useState("kanban");

  const load = async () => {
    const [{ data: inqs }, { data: prods }] = await Promise.all([
      api.get("/inquiries"),
      api.get("/products?active_only=true"),
    ]);
    setInquiries(inqs);
    setProducts(prods);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = inquiries;
    if (filter !== "all") list = list.filter((i) => i.status === filter);
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter((i) =>
        (i.bride_name || "").toLowerCase().includes(s) ||
        (i.groom_name || "").toLowerCase().includes(s) ||
        (i.inquiry_code || "").toLowerCase().includes(s) ||
        (i.whatsapp || "").toLowerCase().includes(s) ||
        (i.event_name || "").toLowerCase().includes(s)
      );
    }
    return list;
  }, [inquiries, filter, search]);

  const grouped = useMemo(() => {
    const map = {}; KANBAN_STATUSES.forEach((s) => (map[s] = []));
    filtered.forEach((i) => { if (map[i.status]) map[i.status].push(i); });
    return map;
  }, [filtered]);

  const submitCreate = async () => {
    try {
      const payload = { ...form, guest_count: Number(form.guest_count) || 0,
        division_id: form.division_id || null,
        lines: form.lines.map((l) => ({ ...l, quantity: Number(l.quantity), unit_price: Number(l.unit_price) })) };
      await api.post("/inquiries", payload);
      toast.success("Inquiry dibuat");
      setOpenCreate(false); setForm(EMPTY); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const updateStatus = async (inq, newStatus, reason = "") => {
    try {
      await api.put(`/inquiries/${inq.id}`, { status: newStatus, change_reason: reason });
      toast.success(`Status → ${INQUIRY_STATUS_LABEL[newStatus]}`); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const convert = async (inq) => {
    if (!window.confirm(`Konversi ${inq.inquiry_code} menjadi Booking? Client baru akan dibuat jika perlu.`)) return;
    try {
      const { data } = await api.post(`/inquiries/${inq.id}/convert`);
      toast.success(`Booking ${data.booking_code} dibuat`);
      nav(`/bookings/${data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (inq) => {
    if (!window.confirm(`Hapus inquiry ${inq.inquiry_code}?`)) return;
    await api.delete(`/inquiries/${inq.id}`);
    toast.success("Inquiry dihapus"); load();
  };

  const addLine = (product) => {
    setForm({ ...form,
      lines: [...(form.lines || []), {
        product_id: product?.id || null,
        name: product?.name || "Custom Item",
        quantity: 1, unit_price: product?.price || 0, notes: ""
      }]});
  };

  return (
    <div className="space-y-5" data-testid="inquiry-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">Master Inquiry</h1>
          <p className="text-sm text-[#5C6066] mt-1">Kelola calon klien dari lead awal sampai konversi ke Booking.</p>
        </div>
        <Button className="bg-brand text-white" onClick={() => { setForm(EMPTY); setOpenCreate(true); }} data-testid="btn-new-inquiry">
          <Plus className="w-4 h-4 mr-1.5" /> Inquiry Baru
        </Button>
      </div>

      <Card className="p-4 border border-[#E5E0D8] bg-white shadow-none flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8C9096]" />
          <Input placeholder="Cari nama klien, kode inquiry, WA..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="inquiry-search"/>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40" data-testid="inquiry-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Status</SelectItem>
            {INQUIRY_STATUSES.map((s) => <SelectItem key={s} value={s}>{INQUIRY_STATUS_LABEL[s]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Tabs value={view} onValueChange={setView}>
          <TabsList>
            <TabsTrigger value="kanban" data-testid="view-kanban">Kanban</TabsTrigger>
            <TabsTrigger value="list" data-testid="view-list">List</TabsTrigger>
          </TabsList>
        </Tabs>
      </Card>

      {view === "kanban" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="inquiry-kanban">
          {KANBAN_STATUSES.map((s) => (
            <div key={s} className="rounded-lg bg-[#FAF6EE] border border-[#E5E0D8] p-3 min-h-[240px]" data-testid={`kanban-col-${s}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-[#1E2022]">{INQUIRY_STATUS_LABEL[s]}</div>
                <Badge variant="outline">{grouped[s].length}</Badge>
              </div>
              <div className="space-y-2">
                {grouped[s].map((i) => (
                  <button key={i.id} onClick={() => setOpenDetail(i)}
                    className="block w-full text-left p-3 rounded-md bg-white border border-[#E5E0D8] hover:border-brand transition-colors" data-testid={`inquiry-card-${i.inquiry_code}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-mono text-[10px] text-brand">{i.inquiry_code}</div>
                      <Badge variant="outline" className="text-[10px]">{i.source}</Badge>
                    </div>
                    <div className="font-medium text-sm mt-1">{i.bride_name} & {i.groom_name || "-"}</div>
                    <div className="text-[11px] text-[#5C6066] mt-0.5">{i.event_date ? formatDate(i.event_date) : "Belum ada tanggal"}</div>
                    {i.subtotal > 0 && <div className="text-[11px] text-[#5C6066] mt-1 font-mono">{formatIDR(i.subtotal)}</div>}
                    {i.package_interest && <div className="text-[11px] text-[#8C9096] mt-1">📦 {i.package_interest}</div>}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Card className="border border-[#E5E0D8] bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-[#8C9096] bg-[#FAF6EE]">
                <th className="text-left px-4 py-3">Kode</th>
                <th className="text-left px-4 py-3">Klien</th>
                <th className="text-left px-4 py-3">Event</th>
                <th className="text-left px-4 py-3">Tanggal</th>
                <th className="text-left px-4 py-3">Sumber</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Estimasi</th>
                <th className="text-right px-4 py-3">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-[#8C9096]">Belum ada inquiry.</td></tr>}
              {filtered.map((i) => (
                <tr key={i.id} className="border-t border-[#F0EBE0] hover:bg-[#FAF6EE]/40">
                  <td className="px-4 py-3 font-mono text-xs text-brand">{i.inquiry_code}</td>
                  <td className="px-4 py-3">{i.bride_name} & {i.groom_name || "-"}<div className="text-[11px] text-[#8C9096]">{i.whatsapp}</div></td>
                  <td className="px-4 py-3">{i.event_name || "-"}</td>
                  <td className="px-4 py-3">{i.event_date ? formatDate(i.event_date) : "-"}</td>
                  <td className="px-4 py-3">{i.source}</td>
                  <td className="px-4 py-3"><StatusBadge status={i.status} label={INQUIRY_STATUS_LABEL[i.status]} /></td>
                  <td className="px-4 py-3 text-right font-mono">{i.subtotal ? formatIDR(i.subtotal) : "-"}</td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="outline" size="sm" onClick={() => setOpenDetail(i)} data-testid={`btn-open-${i.inquiry_code}`}>Detail</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Create Dialog */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="modal-create-inquiry">
          <DialogHeader><DialogTitle>Inquiry Baru</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div><Label>Nama CPW</Label><Input className="mt-1.5" value={form.bride_name} onChange={(e) => setForm({ ...form, bride_name: e.target.value })} data-testid="inq-bride"/></div>
            <div><Label>Nama CPP</Label><Input className="mt-1.5" value={form.groom_name} onChange={(e) => setForm({ ...form, groom_name: e.target.value })} data-testid="inq-groom"/></div>
            <div><Label>WhatsApp</Label><Input className="mt-1.5" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} data-testid="inq-whatsapp"/></div>
            <div><Label>Instagram</Label><Input className="mt-1.5" value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} /></div>
            <div className="col-span-2"><Label>Email</Label><Input className="mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}/></div>
            <div><Label>Nama Acara</Label><Input className="mt-1.5" value={form.event_name} onChange={(e) => setForm({ ...form, event_name: e.target.value })}/></div>
            <div><Label>Tanggal Acara</Label><Input type="date" className="mt-1.5" value={form.event_date || ""} onChange={(e) => setForm({ ...form, event_date: e.target.value })} data-testid="inq-event-date"/></div>
            <div><Label>Venue</Label><Input className="mt-1.5" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })}/></div>
            <div><Label>Kota</Label><Input className="mt-1.5" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}/></div>
            <div><Label>Jumlah Tamu</Label><Input type="number" className="mt-1.5" value={form.guest_count} onChange={(e) => setForm({ ...form, guest_count: e.target.value })}/></div>
            <div>
              <Label>Sumber</Label>
              <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                <SelectTrigger className="mt-1.5" data-testid="inq-source"><SelectValue /></SelectTrigger>
                <SelectContent>{INQUIRY_SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Divisi (opsional)</Label>
              <Select value={form.division_id || "none"} onValueChange={(v) => setForm({ ...form, division_id: v === "none" ? null : v })}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="General"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">General / Semua</SelectItem>
                  {DIVISIONS.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Budget Range</Label><Input className="mt-1.5" value={form.budget_range} onChange={(e) => setForm({ ...form, budget_range: e.target.value })} placeholder="cth: 20-30jt"/></div>
            <div><Label>Paket Diminati</Label><Input className="mt-1.5" value={form.package_interest} onChange={(e) => setForm({ ...form, package_interest: e.target.value })}/></div>
            <div className="col-span-2"><Label>Interest / Kebutuhan</Label><Textarea rows={2} className="mt-1.5" value={form.interest} onChange={(e) => setForm({ ...form, interest: e.target.value })}/></div>
            <div className="col-span-2"><Label>Catatan Internal</Label><Textarea rows={2} className="mt-1.5" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}/></div>
            <div className="col-span-2">
              <Label className="mb-2 inline-block">Preferensi Paket / Layanan (dari Master Pricelist)</Label>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <Select onValueChange={(v) => { const p = products.find((x) => x.id === v); if (p) addLine(p); }}>
                  <SelectTrigger data-testid="inq-line-product"><SelectValue placeholder="+ Tambah dari Pricelist" /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.kind === "package" ? "📦" : "🛍"} {p.name} — {formatIDR(p.price)}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={() => addLine(null)}>+ Custom Item</Button>
              </div>
              {form.lines.length === 0 && <div className="text-xs text-[#8C9096] italic">Belum ada item. Tambahkan dari pricelist atau custom.</div>}
              {form.lines.map((l, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 mb-2 items-start">
                  <Input className="col-span-5" value={l.name} onChange={(e) => { const arr = [...form.lines]; arr[idx].name = e.target.value; setForm({ ...form, lines: arr }); }} />
                  <Input className="col-span-2" type="number" min="1" value={l.quantity} onChange={(e) => { const arr = [...form.lines]; arr[idx].quantity = e.target.value; setForm({ ...form, lines: arr }); }} />
                  <Input className="col-span-3" type="number" value={l.unit_price} onChange={(e) => { const arr = [...form.lines]; arr[idx].unit_price = e.target.value; setForm({ ...form, lines: arr }); }} />
                  <div className="col-span-1 text-right text-xs font-mono self-center">{formatIDR((Number(l.quantity)||0)*(Number(l.unit_price)||0))}</div>
                  <Button variant="ghost" size="icon" className="col-span-1" onClick={() => setForm({ ...form, lines: form.lines.filter((_, i) => i !== idx) })}><Trash2 className="w-4 h-4"/></Button>
                </div>
              ))}
              {form.lines.length > 0 && (
                <div className="text-right text-sm mt-2">Subtotal Estimasi: <span className="font-mono font-bold">{formatIDR(form.lines.reduce((a, l) => a + (Number(l.quantity)||0)*(Number(l.unit_price)||0), 0))}</span></div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenCreate(false)}>Batal</Button>
            <Button className="bg-brand text-white" onClick={submitCreate} disabled={!form.bride_name || !form.whatsapp} data-testid="btn-save-inquiry">Simpan Inquiry</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      {openDetail && (
        <Dialog open={!!openDetail} onOpenChange={() => setOpenDetail(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="modal-inquiry-detail">
            <DialogHeader>
              <DialogTitle>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-brand">{openDetail.inquiry_code}</span>
                  <StatusBadge status={openDetail.status} label={INQUIRY_STATUS_LABEL[openDetail.status]} />
                </div>
              </DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 text-sm py-2">
              <div><div className="text-[10px] uppercase text-[#8C9096]">CPW / CPP</div><div className="font-medium">{openDetail.bride_name} & {openDetail.groom_name || "-"}</div></div>
              <div><div className="text-[10px] uppercase text-[#8C9096]">WhatsApp</div><div className="font-medium">{openDetail.whatsapp}</div></div>
              <div><div className="text-[10px] uppercase text-[#8C9096]">Event</div><div className="font-medium">{openDetail.event_name || "-"} — {openDetail.event_date ? formatDate(openDetail.event_date) : "-"}</div></div>
              <div><div className="text-[10px] uppercase text-[#8C9096]">Venue</div><div className="font-medium">{openDetail.venue || "-"}, {openDetail.location || "-"}</div></div>
              <div><div className="text-[10px] uppercase text-[#8C9096]">Sumber</div><div className="font-medium">{openDetail.source}</div></div>
              <div><div className="text-[10px] uppercase text-[#8C9096]">Budget</div><div className="font-medium">{openDetail.budget_range || "-"}</div></div>
              <div className="col-span-2"><div className="text-[10px] uppercase text-[#8C9096]">Paket Diminati</div><div className="font-medium">{openDetail.package_interest || "-"}</div></div>
              <div className="col-span-2"><div className="text-[10px] uppercase text-[#8C9096]">Catatan</div><div className="text-[#1E2022] whitespace-pre-wrap">{openDetail.notes || "-"}</div></div>
            </div>

            {openDetail.lines?.length > 0 && (
              <Card className="border border-[#E5E0D8] bg-white overflow-hidden">
                <div className="px-4 py-2 text-xs font-semibold uppercase text-[#8C9096] border-b border-[#E5E0D8]">Item Pesanan (Snapshot Pricelist)</div>
                <table className="w-full text-sm">
                  <tbody>
                    {openDetail.lines.map((l) => (
                      <tr key={l.id} className="border-t border-[#F0EBE0]"><td className="p-3">{l.name}</td><td className="p-3 text-center">{l.quantity}×</td><td className="p-3 text-right font-mono">{formatIDR(l.unit_price)}</td><td className="p-3 text-right font-mono font-bold">{formatIDR(l.line_total)}</td></tr>
                    ))}
                    <tr className="bg-[#FAF6EE] border-t border-[#E5E0D8]"><td colSpan={3} className="p-3 text-right font-semibold">Subtotal</td><td className="p-3 text-right font-mono font-bold">{formatIDR(openDetail.subtotal)}</td></tr>
                  </tbody>
                </table>
              </Card>
            )}

            {openDetail.status_history?.length > 0 && (
              <div className="mt-2">
                <div className="text-xs font-semibold uppercase text-[#8C9096] mb-2">Timeline Status</div>
                <div className="space-y-1">
                  {openDetail.status_history.map((h, i) => (
                    <div key={i} className="text-xs flex items-center gap-2">
                      <StatusBadge status={h.status} label={INQUIRY_STATUS_LABEL[h.status]}/>
                      <span className="text-[#8C9096]">{new Date(h.at).toLocaleString("id-ID")}</span>
                      {h.note && <span className="text-[#5C6066]">— {h.note}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <DialogFooter className="flex-wrap gap-2">
              <div className="flex items-center gap-2 mr-auto">
                <Label className="text-xs">Ubah Status:</Label>
                <Select value={openDetail.status} onValueChange={async (v) => { await updateStatus(openDetail, v); const { data } = await api.get(`/inquiries/${openDetail.id}`); setOpenDetail(data); }}>
                  <SelectTrigger className="h-9 w-40" data-testid="inquiry-detail-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{INQUIRY_STATUSES.map((s) => <SelectItem key={s} value={s}>{INQUIRY_STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {canDelete(user) && <Button variant="destructive" size="sm" onClick={() => { remove(openDetail); setOpenDetail(null); }} data-testid="btn-delete-inquiry"><Trash2 className="w-4 h-4 mr-1"/> Hapus</Button>}
              {!openDetail.converted_booking_id && (
                <Button className="bg-brand text-white" onClick={() => convert(openDetail)} data-testid="btn-convert-inquiry">
                  <ArrowRightCircle className="w-4 h-4 mr-1.5"/> Konversi ke Booking
                </Button>
              )}
              <Button variant="outline" onClick={() => window.open(`/print/inquiry/${openDetail.id}`, "_blank")} data-testid="btn-print-inquiry">
                🖨 Cetak / PDF
              </Button>
              {openDetail.whatsapp && (() => {
                const msg = waTemplates.quotationSent(openDetail.bride_name || "Ibu/Bapak", openDetail.inquiry_code);
                return (
                  <a href={waLink(openDetail.whatsapp, msg)} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center h-9 px-3 rounded-md border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-sm font-medium"
                    data-testid="btn-wa-inquiry">
                    <MessageCircle className="w-4 h-4 mr-1.5"/> Kirim WA
                  </a>
                );
              })()}
              {openDetail.converted_booking_id && (
                <Button variant="outline" onClick={() => nav(`/bookings/${openDetail.converted_booking_id}`)}>Buka Booking →</Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
